import { useEffect, useState, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { useRouter } from 'next/router';
import { IRoadshowsFilters } from '@/creditflowresearch/application/Roadshows/vo/IRoadshowsPayload';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { initRoadshowsFilters } from '@/creditflowresearch/application/Roadshows/constants';
import {
  buildIndustryFilters,
  buildParentFilters,
  buildSectorFilters,
} from '@/creditflowresearch/application/helpers/buildFilters';
import { useParentPageContext } from '@/creditflowresearch/application/ParentPageContext';
import Market from '@/creditflowresearch/domain/Market';
import Segment from '@/creditflowresearch/domain/Segment';

const useFilter = (initFilters: IRoadshowsFilters) => {
  const { query: { sponsorName, issuerName, markets, segments, products, regions } } = useRouter();
  const { pageFilters: { productIds, regionIds, industry, sector, includedParentId } } = usePageFiltersContext();
  const { deal } = useParentPageContext();
  const { sectorId, subsectorId } = sector || {};
  const { industryId, subindustryId } = industry || {};
  const [filters, setFilters] = useState<IRoadshowsFilters>(initFilters);
  const marketIds = Market.getIds(markets);
  const segmentIds = Segment.getIds(segments);

  const computedFilters = useMemo(() => {
    if (deal && deal.id) {
      return {
        ...initRoadshowsFilters,
        includedDealIds: [deal.id],
      };
    }

    return {
      ...initRoadshowsFilters,
      includedRegionIds: regions ? regionIds : [],
      includedProductIds: products ? productIds : [],
      includedMarketIds: marketIds,
      includedSegmentIds: segmentIds,
      ...buildParentFilters(includedParentId, sponsorName as string, issuerName as string),
      ...buildSectorFilters(sectorId, subsectorId),
      ...buildIndustryFilters(industryId, subindustryId),
    };
  }, [
    deal?.id,
    regionIds,
    productIds,
    includedParentId,
    sponsorName,
    issuerName,
    sectorId,
    subsectorId,
    industryId,
    subindustryId,
    marketIds,
    segmentIds,
  ]);

  useEffect(() => {
    if (!isEqual(filters, computedFilters)) {
      setFilters(computedFilters);
    }
  }, [computedFilters]);

  return { filters };
};

export default useFilter;
